import { logD, logW } from "@blacknut/logging/dist";
import type {
  GamepadAxis,
  GamepadAxisEvent,
  GamepadButtonEvent,
  GamepadButtonType,
  GamepadTriggerEvent,
  InputProvider,
} from "@blacknut/playerwebrtc";
import { Keyboard, KeyEvents, Keystroke } from "keysim";
import throttle from "lodash/throttle";
import { Subject } from "rxjs";
import Gamepad, { GamepadEvent, GamepadHelper } from "./gamepad";
const keyboard = Keyboard.US_ENGLISH;
const TAG = "Input";

class InputUI {
  private static ID_SEED = 0;
  public buttons: GamepadButtonType[] = [
    "A",
    "B",
    "X",
    "Y",
    "START",
    "LB",
    "RB",
    "LS",
    "RS",
    "DPU",
    "DPD",
    "DPL",
    "DPR",
    "CLK",
  ];
  public axis: GamepadAxis[] = ["LA", "RA"];

  public buttonsUI: GamepadButtonType[] = ["A", "B", "START", "BACK"];
  public axisUI: GamepadAxis[] = ["LA"];
  public dPadUI: GamepadButtonType[] = ["DPU", "DPD", "DPL", "DPR"];

  private gamepadUI: Gamepad | undefined;
  private gamepadGame: Gamepad | undefined;

  private gamepadUISubject = new Subject<GamepadEvent>();
  private gamepadGameSubject = new Subject<GamepadEvent>();

  private _gameMode: boolean = false;
  private gameModeSubject = new Subject<boolean>();
  public get gameMode(): boolean {
    return this._gameMode;
  }
  public set gameMode(v: boolean) {
    if (this._gameMode != v) {
      this._gameMode = v;
      if (!v && this.gamepadGame) {
        this.gamepadGame.pause();
      } else if (v && this.gamepadGame) {
        this.gamepadGame.resume();
      }
      this.gameModeSubject.next(v);
    }
  }

  private _pollingIntervalUI: number | undefined;
  public get pollingIntervalUI(): number | undefined {
    return this._pollingIntervalUI;
  }
  public set pollingIntervalUI(v: number | undefined) {
    this._pollingIntervalUI = v;
    if (this.gamepadUI) {
      this.gamepadUI.pollingInterval = v;
    }
  }

  private _pollingIntervalGame: number | undefined;
  public get pollingIntervalGame(): number | undefined {
    return this._pollingIntervalGame;
  }
  public set pollingIntervalGame(v: number | undefined) {
    this._pollingIntervalGame = v;
    if (this.gamepadGame) {
      this.gamepadGame.pollingInterval = v;
    }
  }

  private _gamepadHelper: GamepadHelper | undefined;
  public get gamepadHelper(): GamepadHelper | undefined {
    return this._gamepadHelper;
  }
  public set gamepadHelper(v: GamepadHelper | undefined) {
    this._gamepadHelper = v;
  }

  private tag: string;
  constructor() {
    InputUI.ID_SEED++;
    this.tag = `input#${InputUI.ID_SEED}`;
  }

  public setupGameGamepad(input: InputProvider) {
    if (!this.gamepadGame) {
      logD(this.tag, "setup Game Gamepad");

      this.gamepadGame = new Gamepad(`${this.tag}(game)`);
      this.gamepadGame.optimizeHold = true;
      this.gamepadGame.pollingInterval = this.pollingIntervalGame;
      this.gamepadGame.gamepadHelper = this.gamepadHelper;
      this.gamepadGame.addConnectListener((e) => input.onGamepadConnect(e));
      this.gamepadGame.addDisconnectListener((e) => input.onGamepadDisconnect(e));

      this.gamepadGame.addInputListener(
        "press release",
        this.buttons,
        (e: GamepadEvent | GamepadTriggerEvent | GamepadAxisEvent) => {
          this.gamepadGameSubject.next(e);
          input.onGamepadButtonEvent(e as GamepadButtonEvent);
        },
      );
      this.gamepadGame.addInputListener(
        "press release",
        "BACK",
        (e: GamepadEvent | GamepadTriggerEvent | GamepadAxisEvent) => {
          this.gamepadGameSubject.next(e);
          input.onGamepadBackEvent(e as GamepadButtonEvent);
        },
      );

      // Analog Axis
      this.gamepadGame.addInputListener(
        "press hold release",
        ["LT", "RT"],
        (e: GamepadEvent | GamepadTriggerEvent | GamepadAxisEvent) =>
          input.onGamepadTrigger(e as GamepadTriggerEvent),
      );
      this.gamepadGame.addInputListener(
        "press hold release",
        this.axis,
        (e: GamepadEvent | GamepadTriggerEvent | GamepadAxisEvent) =>
          input.onGamepadAxis(e as GamepadAxisEvent),
      );
    }
    this.gameMode = true;
  }

  public onGamepadUIEvent() {
    return this.gamepadUISubject.asObservable();
  }
  public onGamepadGameEvent() {
    return this.gamepadGameSubject.asObservable();
  }
  public onGameModeChange() {
    return this.gameModeSubject.asObservable();
  }

  public destroyGameGamepad() {
    logD(this.tag, "teardown Game Gamepad");

    if (this.gamepadGame) {
      this.gamepadGame.removeConnectListener();
      this.gamepadGame.removeDisconnectListener();
      this.gamepadGame.removeInputListener("press release", this.buttons);
      this.gamepadGame.removeInputListener("press release", "BACK");
      this.gamepadGame.removeInputListener("press hold release", ["LT", "RT"]);
      this.gamepadGame.removeInputListener("press hold release", this.axis);
      this.gamepadGame.destroy();
    }
    this.gameMode = false;
    this.gamepadGame = undefined;
  }

  public setupUIGamepad() {
    if (!this.gamepadUI) {
      logD(this.tag, "setup UI Gamepad");
      this.gamepadUI = new Gamepad(`${this.tag}(ui)`, this.gamepadGame);
      this.gamepadUI.optimizeHold = false;
      this.gamepadUI.pollingInterval = this.pollingIntervalUI;
      this.gamepadUI.gamepadHelper = this.gamepadHelper;

      this.gamepadUI.addInputListener(
        "press release",
        this.buttonsUI,
        (e: GamepadEvent) => this.onGamepadButtonUIEvent(e),
      );

      //remove throttle since it depends on player (and we have to much events to handle)
      const holdAxisCb = this.onGamepadAxisUIEvent.bind(this);

      this.gamepadUI.addInputListener("hold", this.axisUI, holdAxisCb);

      const holdDpadCb = throttle(this.onDPadUIEvent.bind(this), 200, {
        trailing: false,
      });
      this.gamepadUI.addInputListener("hold", this.dPadUI, holdDpadCb);
    }
  }

  public destroyGamepadUI() {
    logD(this.tag, "teardown UI Gamepad");

    if (this.gamepadUI) {
      this.gamepadUI.removeInputListener("press release", this.buttonsUI);
      this.gamepadUI.removeInputListener("hold", this.axisUI);
      this.gamepadUI.removeInputListener("hold", this.dPadUI);
      this.gamepadUI.destroy();
    }
    this.gamepadUI = undefined;
  }

  private simulateKey2(action: "enter" | "left" | "right" | "up" | "down" | "back") {
    const focusedElement = document.querySelectorAll(":focus");
    let elem: Element = document.body;
    if (focusedElement.length > 0) {
      elem = focusedElement[0];
    }
    if (action === "enter") {
      if (elem.tagName.toLowerCase() === "button" && (elem as HTMLButtonElement).click) {
        (elem as HTMLButtonElement).click();
      } else {
        // ClickEnter on FocusableElement --> keydown/keyup
        keyboard.dispatchEventsForKeystroke(
          new Keystroke(0, 13),
          elem,
          true,
          KeyEvents.DOWN,
        );
        keyboard.dispatchEventsForKeystroke(
          new Keystroke(0, 13),
          elem,
          true,
          KeyEvents.UP,
        );
      }
    } else if (action === "back") {
      keyboard.dispatchEventsForAction("ctrl+backspace", elem);
    } else {
      keyboard.dispatchEventsForAction(action, elem);
    }
  }
  private onGamepadButtonUIEvent(e: GamepadEvent) {
    this.gamepadUISubject.next(e);

    if (e.type === "press") {
      switch (e.button) {
        case "A":
        case "START":
          this.simulateKey2("enter");
          break;
        case "B":
        case "BACK":
          // const keycode = isTizen() ? 10009 : 27; // Escape if not Tizen
          // this.simulateKey(keycode, "down", { key: "Escape", code: "Escape" });
          // this.simulateKey(keycode, "up", { key: "Escape", code: "Escape" });
          this.simulateKey2("back");
          break;
        case "DPU":
          this.simulateKey2("up");
          break;
        case "DPD":
          this.simulateKey2("down");
          break;
        case "DPL":
          this.simulateKey2("left");
          break;
        case "DPR":
          this.simulateKey2("right");
          break;
      }
    }
  }

  private previousAxisUIValue: Record<number, { x: number; y: number }> = {};
  private onGamepadAxisUIEvent(e: GamepadEvent) {
    this.gamepadUISubject.next(e);
    const THESHOLD = 0.7;
    const x =
      (e.value as number[])[0] < -THESHOLD
        ? -1
        : (e.value as number[])[0] > THESHOLD
        ? 1
        : 0;
    const y =
      (e.value as number[])[1] < -THESHOLD
        ? -1
        : (e.value as number[])[1] > THESHOLD
        ? 1
        : 0;
    const previousAxisUIValue = this.previousAxisUIValue[e.player];
    if (
      !previousAxisUIValue ||
      previousAxisUIValue.x !== x ||
      previousAxisUIValue.y !== y
    ) {
      this.previousAxisUIValue[e.player] = { x, y };
      if (x === -1) {
        this.simulateKey2("left");
      } else if (x === 1) {
        this.simulateKey2("right");
      }
      if (y === -1) {
        this.simulateKey2("up");
      } else if (y === 1) {
        this.simulateKey2("down");
      }
    }
  }

  private onDPadUIEvent(e: GamepadEvent) {
    this.gamepadUISubject.next(e);
    switch (e.button) {
      case "DPU":
        this.simulateKey2("up");
        break;
      case "DPD":
        this.simulateKey2("down");
        break;
      case "DPL":
        this.simulateKey2("left");
        break;
      case "DPR":
        this.simulateKey2("right");
        break;
    }
  }
}

export const inputUI = new InputUI();
